import { format } from "date-fns";
import type { CSSProperties } from "react";
import { api, edenFetch } from "@/lib/eden";
import { queryClient } from "@/lib/queryClient";

/** The two headline reminder types a contact row/form works with. */
export type ReminderKind = "birthday" | "anniversary";

// TanStack Query key roots for the contact surfaces (prefix-matched on invalidation).
export const CONTACTS_QUERY_KEY = "/api/user/contacts";
export const REMINDERS_QUERY_KEY = "/api/user/reminders";
export const GROUPS_QUERY_KEY = "/api/user/groups";
/** The member's own created share links. */
export const SHARES_QUERY_KEY = "/api/user/shares";

/** A contact as the composer's recipient picker sees it (delivery/contacts-dialog.tsx). */
export interface RecipientContact {
  contact_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}
/** Query for the composer's recipient picker: the member's contacts minus their own
 *  self-contact (signup provisions it; it is not someone to send a card to — Erin, 2026-10-09).
 *  Shared by the delivery dialog and the composer's prefetch on mount (2026-11-02). */
export const COMPOSER_CONTACTS_QUERY_KEY = [CONTACTS_QUERY_KEY, "composer"] as const;
export async function fetchComposerContacts(): Promise<{ contacts: RecipientContact[] }> {
  const res = (await edenFetch(api.api.user.contacts.get({ query: {} }))) as {
    contacts: (RecipientContact & { is_self?: boolean })[];
  };
  return { contacts: res.contacts.filter((c) => !c.is_self) };
}

/** Refresh everything a contact mutation touches — saves and deletes cascade to
 *  reminders server-side, and membership edits + deletes move group member counts,
 *  so all three caches always move together. */
export function invalidateContactData(): void {
  void queryClient.invalidateQueries({ queryKey: [CONTACTS_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [REMINDERS_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [GROUPS_QUERY_KEY] });
}

/** Refresh the member's own share links after create/revoke. */
export function invalidateShareData(): void {
  void queryClient.invalidateQueries({ queryKey: [SHARES_QUERY_KEY] });
}

// Row shape of GET /user/contacts (the list projection).
export interface Contact {
  contact_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  relationship_code: string | null;
  matched_user_id: string | null;
  is_self: boolean;
  is_deceased: boolean;
  photo_user_image_id: string | null;
  group_ids: string[];
}

/** The contact roster (optionally one group) — the one typed fetcher for the list;
 *  the server types the rows as t.Any(), so the Contact assertion lives here only. */
export function fetchContacts(group?: string): Promise<{ contacts: Contact[] }> {
  return edenFetch(api.api.user.contacts.get({ query: group ? { group } : {} })) as Promise<{
    contacts: Contact[];
  }>;
}

// Row shape of GET /user/reminders.
export interface Reminder {
  reminder_id: string;
  reminder_type_code: "birthday" | "anniversary" | "custom" | "managed";
  contact_id: string | null;
  event_month: number;
  event_day: number;
  event_year: number | null;
  next_occurrence_on: string | null;
}

// Row shape of GET /user/groups.
export interface ContactGroup {
  contact_group_id: string;
  name: string;
  color: string | null;
  position: number;
  is_system: boolean;
  member_count: number;
}

/** Per-contact rollup of the member's reminders: the two headline types shown on
 *  the row, plus the soonest upcoming occurrence across ALL its reminders (drives
 *  the "next occurrence" sort, same min the server's sort=next uses). */
export interface ContactReminders {
  birthday?: Reminder;
  anniversary?: Reminder;
  soonest?: string;
}

/** Old app's `getPrettyDate` shape: "January 1st, 1990", year omitted when unknown. */
export function formatEventDate(
  reminder: Pick<Reminder, "event_month" | "event_day" | "event_year">,
): string {
  const base = format(new Date(2000, reminder.event_month - 1, reminder.event_day), "MMMM do");
  return reminder.event_year ? `${base}, ${reminder.event_year}` : base;
}

export function fullNameOf(contact: Pick<Contact, "first_name" | "last_name">): string {
  return [contact.first_name, contact.last_name].filter(Boolean).join(" ");
}

/** Black/white text that stays legible on a solid `hex` fill (YIQ luminance).
 *  Threshold nudged to 145 so mid-tone brand colors (green/blue/pink) take white. */
function readableTextColor(hex: string): string {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return "#1a1a1a";
  const int = Number.parseInt(match[1]!, 16);
  const r = (int >> 16) & 0xff;
  const g = (int >> 8) & 0xff;
  const b = int & 0xff;
  return (r * 299 + g * 587 + b * 114) / 1000 >= 145 ? "#1a1a1a" : "#ffffff";
}

/** Inline style for a solid group chip: the group's own color as the fill with
 *  legible text. Returns `undefined` when the group has no color — the caller
 *  falls back to a neutral chip class then. */
export function groupChipStyle(color: string | null): CSSProperties | undefined {
  if (!color) return undefined;
  return { backgroundColor: color, color: readableTextColor(color) };
}
